import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Typography,
    Box,
    Card,
    Grid,
    Chip,
    Button,
    CircularProgress,
    Alert,
    ThemeProvider,
    createTheme,
    CssBaseline,
    Drawer,
    TextField,
    Stack,
    Divider,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import TransferItem from './TransferItem.component';
import { API_BASE_URL } from "../../../utils/constants";

// --- MUI Theme ---
const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: { main: '#90caf9' },
        secondary: { main: '#f48fb1' },
        background: { default: '#121212', paper: '#1e1e1e' },
    },
    typography: { fontFamily: 'Roboto, sans-serif' },
});



const FilterDrawer = ({ open, onClose, onApply, initialFilters }) => {
    const [localFilters, setLocalFilters] = useState(initialFilters);

    useEffect(() => {
        setLocalFilters(initialFilters);
    }, [initialFilters]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setLocalFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleApply = () => {
        onApply(localFilters);
        onClose();
    };

    const handleClear = () => {
        const clearedFilters = { user: '', tokenid: '', description: '' };
        setLocalFilters(clearedFilters);
        onApply(clearedFilters);
        onClose();
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ width: 300, p: 2 }} role="presentation">
                <Typography variant="h6" gutterBottom>Filters</Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                    <TextField name="user" label="User ID" value={localFilters.user} onChange={handleInputChange} variant="outlined" fullWidth />
                    <TextField name="tokenid" label="Token ID" value={localFilters.tokenid} onChange={handleInputChange} variant="outlined" fullWidth />
                    <TextField name="description" label="Description" value={localFilters.description} onChange={handleInputChange} variant="outlined" fullWidth />
                </Stack>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <Button onClick={handleClear} color="secondary">Clear</Button>
                    <Button onClick={handleApply} variant="contained">Apply</Button>
                </Box>
            </Box>
        </Drawer>
    );
};

// --- Main Component ---
const LiveTransfers = () => {
    const [transfers, setTransfers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [isFilterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [filters, setFilters] = useState({ user: '', tokenid: '', description: '' });
    const [countdown, setCountdown] = useState(5);
    const limit = 50;
    const refreshInterval = 5;

    const fetchTransfers = useCallback(async (showSpinner = true) => {
        if (showSpinner) setLoading(true);
        try {
            const url = new URL(`${API_BASE_URL}/transfers`);
            url.searchParams.append('page', page.toString());
            url.searchParams.append('limit', limit.toString());
            // Append filters to the URL if they exist
            Object.entries(filters).forEach(([key, value]) => {
                if (value) url.searchParams.append(key, value);
            });

            const response = await fetch(url);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Network response was not ok' }));
                throw new Error(errorData.message || `HTTP error! status: ${ response.status }`);
            }

            const result = await response.json();
            setTransfers(result);
            setTotal(result.length || 0);
            setError(null);
        } catch (err) {
            console.error("Failed to fetch transfers:", err);
            if (showSpinner) setError(err.message || "Could not load transfers.");
        } finally {
            if (showSpinner) setLoading(false);
        }
    }, [page, filters]); // `filters` is now a dependency

    useEffect(() => {
        fetchTransfers(true);
        const intervalId = setInterval(() => fetchTransfers(false), refreshInterval * 1000);
        return () => clearInterval(intervalId);
    }, [fetchTransfers]);

    // Effect for the countdown timer
    useEffect(() => {
        setCountdown(refreshInterval); // Reset countdown on page or filter change
        const timer = setInterval(() => {
            setCountdown(prev => (prev > 1 ? prev - 1 : refreshInterval));
        }, 1000);
        return () => clearInterval(timer);
    }, [page, filters]);

    const handleApplyFilters = (newFilters) => {
        setPage(1); // Reset to page 1 when filters change
        setFilters(newFilters);
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <Container maxWidth="md" sx={{
            py: 2,
            height: "100%",
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                <div>
                    <Typography variant="h5" component="h1" gutterBottom sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Transactions</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                        A live log of recent transactions. Refreshing in {countdown}s...
                    </Typography>
                </div>
                <Button variant="outlined" startIcon={<FilterListIcon />} onClick={() => setFilterDrawerOpen(true)} size="small" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>Filters</Button>
            </Box>

            <FilterDrawer open={isFilterDrawerOpen} onClose={() => setFilterDrawerOpen(false)} onApply={handleApplyFilters} initialFilters={filters} />

            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden'
            }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                ) : (
                    <>
                        {transfers.length > 0 ? (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                flex: 1,
                                minHeight: 0,
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                scrollbarWidth: 'none',
                                '&::-webkit-scrollbar': {
                                    display: 'none',
                                },
                            }}>
                                {transfers.map((transfer, index) => <TransferItem key={`${ transfer.tokenid }-${ index }`} transfer={transfer} />)}
                            </Box>
                        ) : (
                            <Card sx={{ p: 2, textAlign: 'center' }}>
                                <Typography color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>No transfers found for the current filters.</Typography>
                            </Card>
                        )}
                        {/* {total > limit && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                    <Button variant="outlined" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} size="small" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>Previous</Button>
                                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>Page {page} of {totalPages}</Typography>
                                    <Button variant="outlined" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} size="small" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>Next</Button>
                                </Box>
                            )} */}
                    </>
                )}
            </Box>
        </Container>
    );
};

export default LiveTransfers;
